import { ModalForm } from 'framework/form';
import { Modal } from 'framework/ui/ui-builder';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS, useProjectSecurityContext } from 'domain/shared/newProject';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import useAssumptionsService from '../assumptionsService';
import { modalActionsSx } from '../shared/modalActionsSx';
import { AssumptionFormValues, AssumptionType } from '../types';
import AssumptionForm from './AssumptionForm';
import { assumptionsValidation } from './assumptionsValidation';
import { buildUpdatePayload } from './buildAssumptionPayload';

interface UpdateAssumptionModalProps {
  assumption: AssumptionType | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (assumption: AssumptionType) => void;
}

const UpdateAssumptionModal = ({ assumption, isOpen, onClose, onSaved }: UpdateAssumptionModalProps) => {
  const { updateAssumption, changeAssumptionStatus } = useAssumptionsService();
  const { isAccessGranted } = useProjectSecurityContext();
  const { getUserAlias } = useAuthenticationProvider();

  const isManage = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.MANAGE);
  const isReview = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.REVIEW);
  const isFull = isManage && !isReview;
  const isStatusOnly = !isManage && !isReview;

  if (!assumption) return null;

  return (
    <Modal isOpen={isOpen} onToggle={onClose} title="Update assumption">
      <ModalForm
        modalOpen={isOpen}
        closeModal={onClose}
        submitButtonText="Update"
        actionsSx={modalActionsSx}
        getDataFunc={() => {
          const { description, assignedRole, status, exceptionStatus, exceptionReason, exceptionUntilDate } =
            assumption;
          return Promise.resolve({
            description: description ?? '',
            assignedRole: assignedRole ?? null,
            status: status ?? '',
            requestException: Boolean(exceptionStatus),
            exceptionReason: exceptionReason ?? '',
            exceptionUntilDate: exceptionUntilDate ?? null,
          });
        }}
        submitFormFunc={async values => {
          const formValues = values as AssumptionFormValues;
          const updatedBy = getUserAlias() ?? undefined;

          if (isStatusOnly) {
            return {
              result: await changeAssumptionStatus(assumption.id, {
                status: formValues.status,
              }),
            };
          }

          const payload = buildUpdatePayload(assumption, formValues, isFull, updatedBy);
          return { result: await updateAssumption(assumption.id, payload) };
        }}
        afterSubmit={{
          message: 'You are awesome! Assumption has been successfully updated.',
          execute: (response: { result: AssumptionType }) => onSaved(response.result),
        }}
        getValidationSchemaFunc={() => Promise.resolve(assumptionsValidation)}
      >
        <AssumptionForm />
      </ModalForm>
    </Modal>
  );
};

export default UpdateAssumptionModal;
